import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface BadgeProps {
  children: ReactNode;
  variant?: 'content-type' | 'audience' | 'topic' | 'clinical-area' | 'default';
  color?: string;
  className?: string;
  icon?: ReactNode;
  to?: string;
  'aria-label'?: string;
}

const contentTypeColors: Record<string, string> = {
  'Toolkit_Guideline': 'bg-content-toolkit',
  'Template_Form': 'bg-content-template',
  'Educational_Training': 'bg-content-educational',
  'Audit_Report': 'bg-content-audit',
  'Event_Webinar': 'bg-content-event',
  'Publication_Article': 'bg-content-publication',
  'Landing_Portal': 'bg-content-landing',
  'Reference_External': 'bg-content-reference',
};

export default function Badge({
  children,
  variant = 'default',
  color,
  icon,
  className = '',
  to,
  'aria-label': ariaLabel,
}: BadgeProps) {
  const baseStyles = 'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold';

  let variantStyles = '';
  if (variant === 'content-type') {
    const bgColor = color && contentTypeColors[color]
      ? contentTypeColors[color]
      : 'bg-gray-500';
    variantStyles = `${bgColor} text-white uppercase tracking-wide`;
  } else if (variant === 'audience') {
    variantStyles = 'bg-gray-100 text-gray-700';
  } else if (variant === 'topic' || variant === 'clinical-area') {
    variantStyles = 'bg-gray-100 text-gray-700 hover:bg-gray-200';
  } else {
    variantStyles = 'bg-gray-100 text-gray-700';
  }

  const combined = `${baseStyles} ${variantStyles} ${to ? 'focus-ring' : ''} ${className}`;
  const content = (
    <>
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span>{children}</span>
    </>
  );

  if (to) {
    return (
      <Link to={to} className={combined} aria-label={ariaLabel}>
        {content}
      </Link>
    );
  }

  return <span className={combined}>{content}</span>;
}
